// src/components/sops/SopDetailsPage.js — sopweb-bdb7afcd (SOPs Library, Seam 2)
//
// One SOP: title, version, uploaded by / date, deadline; Open PDF, Replace file, Edit people &
// details, Archive — or, when archived, Restore (if its folder is archived the server says "Restore or
// choose a live folder first." and this page offers a folder choice). Tabs: People & reading (access
// now; reading status arrives in Seam 3), Versions (every version; Managers and up can open old
// ones), Where it shows (catalog items).
// READS: the SOP (1), its versions (one each), its access records (one each), the folders (one each,
// for the folder path and the restore choice) and the linked catalog items' names (one each, ≤50).
// sopread-7e4c2a93 — People & reading now shows each person's reading (Read / Signed vN · date, Not
// read yet · due date, Overdue N days) and Last opened, from those SAME access records (no new reads).
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, ExternalLink, Upload, Pencil, Archive, RotateCcw, Download } from 'lucide-react';
import { Button, Modal, Notice, OPENED_NOTE, downloadedNote, inputClass } from './SopUi';
import SopReplaceFileModal from './SopReplaceFileModal';
import {
    loadSop, loadVersions, loadAccess, loadFolders, loadCatalogNames, archiveSop, openSopInNewTab, downloadSop, friendlyError,
} from './sopsApi';
import {
    foldersById, folderPathText, liveFolderList, readerRows, deadlineText, dateText, formatBytes, isRestoreFolderRefusal,
    CONFIRM_OPTIONS, REREAD_OPTIONS, SOPS_PATHS, readerStatus, lastOpenedText, readStatusText,
} from '../../utils/sopsUi';

const TONE = { ok: 'text-green-700', warn: 'text-amber-800', bad: 'text-red-700 font-medium', muted: 'text-gray-400' };

const TABS = [{ key: 'people', label: 'People & reading' }, { key: 'versions', label: 'Versions' }, { key: 'where', label: 'Where it shows' }];
const indent = (depth) => `${'   '.repeat(depth)}${depth > 0 ? '└ ' : ''}`;

export default function SopDetailsPage({ orgId, sopId, users, timeZone }) {
    const navigate = useNavigate();
    const location = useLocation();
    const [data, setData] = useState(null);
    const [status, setStatus] = useState('loading');
    const [error, setError] = useState('');
    const [tab, setTab] = useState('people');
    const [notice, setNotice] = useState(() => {
        const st = location.state || {};
        if (st.peopleNotSaved) return { tone: 'warn', text: `The SOP was saved, but the people weren’t${st.reason ? ` (${st.reason})` : ''}. Click Edit people & details to add them.` };
        if (st.saved === 'created') return { tone: 'ok', text: 'SOP saved.' };
        if (st.saved === 'updated') return { tone: 'ok', text: 'Changes saved.' };
        return null;
    });
    const [dialog, setDialog] = useState(null); // 'replace' | 'archive' | 'restore' | 'restoreFolder'
    const [busy, setBusy] = useState(false);
    const [restoreFolderId, setRestoreFolderId] = useState('');
    const [dialogError, setDialogError] = useState('');

    const reload = useCallback(async () => {
        try {
            const [sop, versions, access, folders] = await Promise.all([loadSop(orgId, sopId), loadVersions(orgId, sopId), loadAccess(orgId, sopId), loadFolders(orgId)]);
            if (!sop) { setStatus('missing'); return; }
            const catalog = await loadCatalogNames(orgId, sop.catalogServiceIds || []);
            setData({ sop, versions, access, folders, catalog });
            setStatus('done');
        } catch (e) {
            console.warn('sopweb: details load failed', e);
            setStatus('error'); setError(friendlyError(e, 'This SOP couldn’t be loaded. Please try again.'));
        }
    }, [orgId, sopId]);
    useEffect(() => { reload(); }, [reload]);
    // Drop the one-time banner from the history entry so a reload doesn't show it again.
    useEffect(() => { if (location.state) navigate(location.pathname, { replace: true, state: null }); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const readers = useMemo(() => (data ? readerRows(data.access, users) : []), [data, users]);
    const byId = useMemo(() => (data ? foldersById(data.folders) : new Map()), [data]);
    const liveFolders = useMemo(() => (data ? liveFolderList(data.folders) : []), [data]);

    if (status === 'loading') return <p className="p-20 text-center text-gray-500 animate-pulse">Loading…</p>;
    if (status === 'missing') return <div className="p-8 max-w-xl mx-auto"><Notice tone="error">That SOP doesn’t exist.</Notice><Button className="mt-3" onClick={() => navigate(SOPS_PATHS.library)}>Back to SOPs</Button></div>;
    if (status === 'error') return <div className="p-8 max-w-xl mx-auto"><Notice tone="error">{error} <button type="button" className="underline" onClick={reload}>Try again</button></Notice></div>;

    const { sop, versions, catalog } = data;
    const current = versions.find((v) => v.id === sop.currentVersionId) || versions[0] || null;
    const archived = sop.archived === true;

    const open = async (versionId) => {
        setNotice(null);
        try { await openSopInNewTab(sop.id, versionId); setNotice({ tone: 'info', text: OPENED_NOTE }); }
        catch (e) { setNotice({ tone: 'error', text: friendlyError(e, 'The SOP couldn’t be opened. Please try again.') }); }
    };
    // sopweb-bdb7afcd — Download (current version, or one version from the Versions tab).
    const download = async (versionId) => {
        setNotice(null);
        try { const res = await downloadSop(sop.id, versionId); setNotice({ tone: 'info', text: downloadedNote(res.fileName) }); }
        catch (e) { setNotice({ tone: 'error', text: friendlyError(e, 'The SOP couldn’t be downloaded. Please try again.') }); }
    };
    const closeDialog = () => { if (!busy) { setDialog(null); setDialogError(''); } };
    const doArchive = async () => {
        setBusy(true); setDialogError('');
        try { await archiveSop(sop.id, true); setDialog(null); setNotice({ tone: 'ok', text: 'SOP archived. Staff can no longer open it. Find it under Archived in the library.' }); await reload(); }
        catch (e) { setDialogError(friendlyError(e, 'The SOP couldn’t be archived. Please try again.')); }
        setBusy(false);
    };
    const doRestore = async (folderId) => {
        setBusy(true); setDialogError('');
        try {
            await archiveSop(sop.id, false, folderId || undefined);
            setDialog(null); setNotice({ tone: 'ok', text: 'SOP restored. People with access can open it again.' });
            await reload();
        } catch (e) {
            if (!folderId && isRestoreFolderRefusal(e)) {
                setRestoreFolderId(''); setDialog('restoreFolder');
            } else setDialogError(friendlyError(e, 'The SOP couldn’t be restored. Please try again.'));
        }
        setBusy(false);
    };

    return (
        <div className="p-4 md:p-6 max-w-5xl mx-auto">
            <button type="button" onClick={() => navigate(SOPS_PATHS.library)} className="mb-3 inline-flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900"><ArrowLeft size={16} /> SOPs</button>
            {notice && <div className="mb-4"><Notice tone={notice.tone} onClose={() => setNotice(null)}>{notice.text}</Notice></div>}
            {archived && <div className="mb-4"><Notice tone="warn">This SOP is archived. Staff can’t open it. Restore it to make it available again.</Notice></div>}

            <div className="rounded-xl border bg-white p-4 md:p-5">
                <div className="flex flex-wrap items-start gap-3">
                    <div className="mr-auto min-w-0">
                        <h1 className="text-2xl font-bold text-[#12192D] break-words">{sop.title}</h1>
                        <p className="mt-1 text-sm text-gray-600">
                            {folderPathText(byId, sop.folderId) || 'No folder'} · Version {sop.currentVersionNumber || 1}
                            {current && <> · Uploaded by {current.uploadedBy?.name || 'someone'}{current.uploadedAt ? ` on ${dateText(current.uploadedAt)}` : ''}</>}
                        </p>
                        <p className="mt-1 text-sm text-gray-600">
                            Must-read deadline: {deadlineText(sop.daysToRead)} · {(CONFIRM_OPTIONS.find((o) => o.value === sop.confirmType) || CONFIRM_OPTIONS[0]).label} · Read again: {(REREAD_OPTIONS.find((o) => o.value === (sop.rereadMonths ?? null)) || REREAD_OPTIONS[0]).label}
                        </p>
                        {sop.description && <p className="mt-2 text-sm text-gray-700 whitespace-pre-line">{sop.description}</p>}
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <Button variant="primary" onClick={() => open()}><ExternalLink size={16} /> Open PDF</Button>
                        <Button onClick={() => download()}><Download size={16} /> Download</Button>
                        {!archived && <Button onClick={() => setDialog('replace')}><Upload size={16} /> Replace file</Button>}
                        {!archived && <Button onClick={() => navigate(`/sops/${sop.id}/edit`)}><Pencil size={16} /> Edit people & details</Button>}
                        {!archived && <Button variant="danger" onClick={() => setDialog('archive')}><Archive size={16} /> Archive</Button>}
                        {archived && <Button variant="primary" disabled={busy} onClick={() => doRestore()}><RotateCcw size={16} /> {busy ? 'Restoring…' : 'Restore'}</Button>}
                    </div>
                </div>
                {archived && dialogError && !dialog && <div className="mt-3"><Notice tone="error">{dialogError}</Notice></div>}
            </div>

            <div className="mt-4 rounded-xl border bg-white">
                <div className="flex border-b overflow-x-auto" role="tablist">
                    {TABS.map((t) => (
                        <button key={t.key} type="button" role="tab" aria-selected={tab === t.key} onClick={() => setTab(t.key)}
                            className={`px-4 py-3 text-sm whitespace-nowrap border-b-2 -mb-px ${tab === t.key ? 'border-[#5F4EC9] text-[#5F4EC9] font-medium' : 'border-transparent text-gray-600 hover:text-gray-900'}`}>{t.label}</button>
                    ))}
                </div>

                {tab === 'people' && (
                    <div className="p-4">
                        <p className="mb-3 text-sm text-gray-600">Owners, Admins and Managers can always open it.{sop.mustReadCount > 0 && <> Version {sop.currentVersionNumber || 1}: <strong>{readStatusText(sop)}</strong>.</>}</p>
                        {readers.length === 0 ? <p className="text-sm text-gray-500">Nobody else has been given access yet.</p> : (
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead className="text-left text-xs uppercase tracking-wide text-gray-500"><tr className="border-b"><th className="py-2 pr-3 font-medium">Person</th><th className="py-2 pr-3 font-medium">Access</th><th className="py-2 pr-3 font-medium">Reading</th><th className="py-2 font-medium">Last opened</th></tr></thead>
                                    <tbody>
                                        {readers.map((r) => (
                                            <tr key={r.uid} className="border-b last:border-0">
                                                <td className="py-2 pr-3">{r.name}{r.inactive && <span className="ml-2 rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">no longer active</span>}</td>
                                                <td className="py-2 pr-3">{r.access}</td>
                                                {(() => { const st = readerStatus(r.record, new Date(), timeZone); return <td className={`py-2 pr-3 ${TONE[st.tone]}`}>{st.text}</td>; })()}
                                                <td className="py-2 text-gray-600 whitespace-nowrap">{lastOpenedText(r.record)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}

                {tab === 'versions' && (
                    <ul className="divide-y">
                        {versions.map((v) => (
                            <li key={v.id} className="flex flex-wrap items-center gap-3 p-4 text-sm">
                                <div className="mr-auto min-w-0">
                                    <div className="font-medium text-[#12192D]">Version {v.versionNumber}{v.id === sop.currentVersionId && <span className="ml-2 rounded-full bg-[#5F4EC9]/10 px-2 py-0.5 text-xs text-[#5F4EC9]">Current</span>}</div>
                                    <div className="text-gray-600">{v.fileName}{v.sizeBytes ? ` · ${formatBytes(v.sizeBytes)}` : ''} · {v.uploadedBy?.name || 'someone'}{v.uploadedAt ? `, ${dateText(v.uploadedAt)}` : ''}</div>
                                    {v.changeNote && <div className="mt-1 text-gray-700">What changed: {v.changeNote}</div>}
                                </div>
                                <div className="flex gap-2">
                                    <Button onClick={() => open(v.id)}><ExternalLink size={14} /> Open</Button>
                                    <Button onClick={() => download(v.id)} aria-label={`Download version ${v.versionNumber}`}><Download size={14} /> Download</Button>
                                </div>
                            </li>
                        ))}
                    </ul>
                )}

                {tab === 'where' && (
                    <div className="p-4 text-sm">
                        <p className="mb-3 text-gray-600">Jobs with these catalog items will show this SOP. (Showing SOPs on jobs comes in a later update.)</p>
                        {catalog.length === 0 ? <p className="text-gray-500">No catalog items linked.{!archived && ' Add some with Edit people & details.'}</p> : (
                            <div className="flex flex-wrap gap-2">
                                {catalog.map((c) => <span key={c.id} className={`rounded-full border px-3 py-1 ${c.missing ? 'border-amber-300 bg-amber-50 text-amber-900' : 'border-[#5F4EC9]/30 bg-[#5F4EC9]/5'}`}>{c.name}</span>)}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {dialog === 'replace' && (
                <SopReplaceFileModal sop={sop} onClose={() => setDialog(null)}
                    onDone={async (res) => { setDialog(null); setNotice({ tone: 'ok', text: `Version ${res.versionNumber} saved.` }); await reload(); setTab('versions'); }} />
            )}
            {dialog === 'archive' && (
                <Modal title="Archive SOP" onClose={closeDialog}
                    footer={<><Button onClick={closeDialog} disabled={busy}>Cancel</Button><Button variant="danger" onClick={doArchive} disabled={busy}>{busy ? 'Archiving…' : 'Archive SOP'}</Button></>}>
                    <p className="text-sm text-gray-700">Archive <strong>{sop.title}</strong>? Staff won’t be able to open it, and it leaves the library list. Nothing is deleted — you can restore it from <strong>Archived</strong>.</p>
                    {dialogError && <Notice tone="error">{dialogError}</Notice>}
                </Modal>
            )}
            {dialog === 'restoreFolder' && (
                <Modal title="Choose a folder" onClose={closeDialog}
                    footer={<><Button onClick={closeDialog} disabled={busy}>Cancel</Button><Button variant="primary" onClick={() => (restoreFolderId ? doRestore(restoreFolderId) : setDialogError('Choose a folder.'))} disabled={busy}>{busy ? 'Restoring…' : 'Restore here'}</Button></>}>
                    <p className="text-sm text-gray-700">The folder this SOP was in ({folderPathText(byId, sop.folderId) || 'deleted'}) is archived. Choose a live folder to restore it into — or restore that folder first from <strong>Archived</strong> in the library.</p>
                    <div>
                        <label htmlFor="sop-restore-folder" className="text-sm font-medium text-[#12192D]">Folder <span className="ml-1 text-xs font-normal text-[#5F4EC9]">Required</span></label>
                        <select id="sop-restore-folder" className={`${inputClass} mt-1`} value={restoreFolderId} onChange={(e) => setRestoreFolderId(e.target.value)}>
                            <option value="">Choose a folder…</option>
                            {liveFolders.map((f) => <option key={f.id} value={f.id}>{indent(f.depth)}{f.name}</option>)}
                        </select>
                        {liveFolders.length === 0 && <p className="mt-1 text-xs text-gray-500">There are no live folders. Make one in the library first.</p>}
                    </div>
                    {dialogError && <Notice tone="error">{dialogError}</Notice>}
                </Modal>
            )}
        </div>
    );
}
